import "./ManualEmail.scss"

import { Accordion, FooterVariant, MenuItem, Tag } from "@skylead/component-library"
import classNames from "classnames"
import _ from "lodash"
import React, { useEffect, useRef, useState } from "react"
import { Dropdown } from "react-bootstrap"
import { useDispatch, useSelector } from "react-redux"

import {
  clearAllEmailAccounts,
  getAllEmailAccounts,
  getEmailAccountSettings,
  getSupportedCampaignTags,
  sendEmail,
  updateFormField,
  updateFormFields,
} from "../../../redux/actions"
import dataUtils from "../../../utils/data/data-utils"
import { useGlobalTranslation, useOnClickOutside } from "../../../utils/hook"
import { checkIfEmail, validateEmailBccList } from "../../../utils/validation/validation-utils"
import Button, { ButtonVariant } from "../../atoms/Button"
import LangText from "../../atoms/LangText"
import Modal, { ModalVariant } from "../../atoms/Modal"
import SavedReply from "../../atoms/SavedReply"
import EmailSignature from "../../layouts/EmailSignature"
import InputGroup from "../InputGroup"
import TagInput from "../TagInput"

const ManualEmail = ({ closeManualEmail }) => {
  const manualEmailRef = useRef()

  const dispatchRedux = useDispatch()
  const { tt } = useGlobalTranslation()
  const { accountSignature, formData, allEmailAccounts } = useSelector(state => ({
    accountSignature: state.email.selectedEmailAccountSettings.accountSignature,
    formData: state.forms.formData,
    allEmailAccounts: state.email,
  }))

  const [validatedEmails, setValidatedEmails] = useState([])
  const [emailToFocused, setEmailToFocused] = useState(true)
  const [emailToValid, setEmailToValid] = useState(false)
  const [signature, setSignature] = useState(null)
  const [open, setOpen] = useState(false)
  const [bccInput] = useState(true)
  const [selectedFromEmail, setSelectedFromEmail] = useState({})
  const [selectedMailbox, setSelectedMailbox] = useState()
  const [showModal, setShowModal] = useState(false)

  const showModalRef = useRef()
  useOnClickOutside(showModalRef, () => setShowModal(false))

  const manualEmailClass = classNames("manual-email", {})

  const emailToClass = classNames("manual-email__to", {
    focused: emailToFocused,
    empty: !formData.manualEmailTo,
  })

  useEffect(() => {
    dispatchRedux(getSupportedCampaignTags(tt))
  }, [])

  const validateEmail = () => {
    if (formData.manualEmailTo) {
      const isEmailToValid = checkIfEmail(formData.manualEmailTo)
      setEmailToValid(isEmailToValid)
      setEmailToFocused(false)
    }
  }

  const validateEmailBcc = newMails => {
    if (newMails.indexOf("") === -1) {
      const validatedEmail = newMails.map(email => {
        const isValid = checkIfEmail(email)
        return {
          email,
          valid: isValid,
        }
      })
      setValidatedEmails(validatedEmail)
      dispatchRedux(updateFormField("manualEmailBcc", _.map(validatedEmail, "email")))
    }
  }

  const handleCloseManualEmail = () => {
    closeManualEmail()
    dispatchRedux(
      updateFormFields({
        manualEmailTo: "",
        manualEmailBcc: "",
        manualEmailSubject: "",
        manualEmailMessage: "",
      }),
    )
  }

  const newOnTagChange = newMails => {
    validateEmailBcc(
      newMails.map(newMail => {
        if (typeof newMail === "string") {
          return newMail
        }
        return newMail.email
      }),
    )
  }
  const handleAction = async mailbox => {
    setSelectedFromEmail(mailbox)
    await dispatchRedux(getEmailAccountSettings(mailbox.mailboxId))
    dispatchRedux(updateFormField("signature", mailbox.accountSignature?.[0]?.signature))
    setSignature(undefined)
  }

  const checkMessage = !!(
    selectedFromEmail.emailAddress &&
    selectedFromEmail.cooldown <= Date.now() &&
    formData.manualEmailTo &&
    checkIfEmail(formData.manualEmailTo) &&
    (formData.manualEmailBcc?.length > 0 ? validateEmailBccList(formData.manualEmailBcc) : true) &&
    formData.manualEmailMessage &&
    formData.manualEmailMessage !== "<p><br></p>" &&
    formData.manualEmailSubject
  )

  const removeToEmail = () => {
    setEmailToFocused(true)
    dispatchRedux(updateFormField("manualEmailTo", ""))
  }

  useEffect(() => {
    dispatchRedux(clearAllEmailAccounts())
    let query = "?sortOrder=ASC&"
    query += "&status=ACTIVE"
    query += "&data=extended"
    dispatchRedux(getAllEmailAccounts(query))
  }, [])

  const allEmailAccountsMenuItems = allEmailAccounts?.allEmailAccounts?.mailboxes?.map(mailbox => (
    <Dropdown.Item
      key={mailbox.mailboxId}
      onClick={() => {
        setSelectedMailbox(mailbox)
        handleAction(mailbox, "mailbox")
      }}
    >
      <MenuItem label={mailbox.emailAddress} />
    </Dropdown.Item>
  ))

  return (
    <Modal
      show
      type={ModalVariant.LARGE}
      title={tt("send-email")}
      content={
        <div className={manualEmailClass} ref={manualEmailRef}>
          <div className="manual-email__body">
            <div className="manual-email__from">
              <InputGroup
                field="selectedEmail"
                label={tt("from")}
                placeholder={tt("choose-an-option")}
                type="select"
                isDropdownMenu
                dropdownSelectedItem={selectedMailbox?.emailAddress}
              >
                {allEmailAccountsMenuItems}
              </InputGroup>
            </div>

            <Accordion>
              <div className={emailToClass}>
                <InputGroup
                  type="email"
                  field="manualEmailTo"
                  onBlur={validateEmail}
                  inputProps={{
                    onBlur: validateEmail,
                    onFocus: () => setEmailToFocused(true),
                  }}
                  label={tt("label.to")}
                />

                <div
                  className="manual-email__to__formatted"
                  onClick={() => setEmailToFocused(true)}
                >
                  {!!formData.manualEmailTo && !emailToFocused && (
                    <Tag
                      tagName={formData.manualEmailTo}
                      isValid={emailToValid}
                      id={2}
                      removeTag={removeToEmail}
                      tagClass="bcc-tag"
                    />
                  )}
                </div>
                <Button variant={ButtonVariant.GHOST} onClick={() => setOpen(!open)}>
                  <LangText langKey="bcc" />
                </Button>
              </div>

              <Accordion.Collapse isOpen={open}>
                <div className="email-bcc-wrapper">
                  <TagInput
                    tagInputEmailBcc={bccInput}
                    tags={validatedEmails}
                    placeholder={tt("bcc")}
                    onTagChange={newOnTagChange}
                    onBlur={newOnTagChange}
                    isOnBlur
                  />
                </div>
              </Accordion.Collapse>
            </Accordion>

            <div className="manual-email__subject">
              <InputGroup field="manualEmailSubject" type="text" label={tt("subject")} />
            </div>
            <div className="manual-email__message">
              <InputGroup
                field="manualEmailMessage"
                type="html"
                label={tt("email-message")}
                placeholder={tt("enter-your-email-message")}
                inputGroupClassName="rich-text-input"
                onBlur={validateEmail}
                inputProps={{
                  onBlur: validateEmail,
                  theme: "snow",
                  modules: {
                    clipboard: {
                      matchVisual: false,
                    },
                  },
                  formats: [
                    "header",
                    "font",
                    "size",
                    "bold",
                    "italic",
                    "underline",
                    "strike",
                    "blockquote",
                    "list",
                    "bullet",
                    "indent",
                    "link",
                    "image",
                    "color",
                    "background",
                    "align",
                    "clean",
                  ],
                }}
                actionBar
                signatureActionBar={
                  <EmailSignature
                    onChange={id => setSignature(id)}
                    signature={signature}
                    selectedFromEmail={selectedFromEmail.emailAddress}
                    accountSignature={accountSignature}
                  />
                }
                addEmojiActionBar={emoji =>
                  dispatchRedux(
                    updateFormField(
                      "manualEmailMessage",
                      `${formData.manualEmailMessage || ""}${emoji.native}`,
                    ),
                  )
                }
                savedRepliesActionBar={
                  <div ref={showModalRef} className="saved-reply">
                    <div className="saved-reply-icon-wrapper">
                      <SavedReply
                        disableChat={false}
                        showModal={showModal}
                        setShowModal={setShowModal}
                        manualEmail
                        isEmail
                      />
                    </div>
                  </div>
                }
              />
            </div>
          </div>
        </div>
      }
      footerVariant={FooterVariant.TRANSACTIONAL}
      primaryBtn={{
        label: tt("send"),
        onClick: () => {
          dispatchRedux(
            sendEmail(
              tt,
              formData.manualEmailTo,
              formData.manualEmailSubject,
              dataUtils.createHTMLEmailTemplate(formData.manualEmailMessage),
              signature || undefined,
              formData.manualEmailBcc,
              selectedFromEmail.mailboxId,
            ),
          )
          handleCloseManualEmail()
        },
        disabled: !checkMessage,
      }}
      secondaryBtn={{ label: tt("close"), onClick: handleCloseManualEmail }}
      noHideOnSubmit
    />
  )
}

// ManualEmail.propTypes = {
//   closeManualEmail: PropTypes.func.isRequired,
// }

export default ManualEmail
